import {
  type AccountInfo,
  CacheLookupPolicy,
  InteractionRequiredAuthError,
  type Configuration,
  PublicClientApplication,
} from '@azure/msal-browser'
import { v1UserControllerGetMyUser } from '~/generated/api'
import { isLocalAuthEnabled, isPublicLocalAuthRoute, LOCAL_AUTH_TOKEN, LOCAL_AUTH_USER } from '~/utils/localAuth'
import { type AdvisorCountryParams, fetchAdvisorCountry } from '~/utils/advisorCountry'

interface AuthenticationProvider extends Record<string, unknown> {
  isLocalAuth: boolean
  login: () => Promise<void>
  /**
   * `interactive: false` returns null instead of redirecting to sign-in or
   * leaving the page, for background work that must not interrupt the user.
   */
  acquireTokenSilent: (options?: { interactive?: boolean }) => Promise<{ accessToken: string } | null | undefined>
  getAccounts: () => Array<AccountInfo | typeof LOCAL_AUTH_USER>
  logout: () => Promise<void>
}

export default defineNuxtPlugin<AuthenticationProvider>(async () => {
  const appConfig = useAppConfig()
  const advisorCountry = useState<AdvisorCountryParams>('advisor-country', () => ({}))
  advisorCountry.value = {}
  clearAnalyticsUserId()
  const {
    currentUser,
    authError,
    userContext,
    showAdminInterface,
    attemptedPath,
  } = storeToRefs(useAuthStore())
  const config: Configuration = {
    auth: {
      clientId: appConfig.AZURE_CLIENT_ID,
      authority: `https://login.microsoftonline.com/organizations/`,
      redirectUri: `${window.location.origin}/`,
      postLogoutRedirectUri: `${window.location.origin}/`,
    },
  }

  const graphConfig = {
    graphMeEndpoint: 'https://graph.microsoft.com/v1.0/me',
  }

  const fetchUserContext = async (accessToken: string) => {
    const { data } = await v1UserControllerGetMyUser({
      auth: accessToken,
      baseUrl: appConfig.DEV_ENDPOINT,
      throwOnError: true,
    })

    return takeAnalyticsUserId(data) ?? null
  }

  const isLocalAuth = isLocalAuthEnabled(
    import.meta.dev,
    appConfig.ADVISOR_LOCAL_AUTH_ENABLED,
    window.location.hostname,
    appConfig.DEV_ENDPOINT,
  )

  if (isLocalAuth) {
    const isPublicReport = () => isPublicLocalAuthRoute(window.location.pathname, window.location.search)
    const clearLocalSession = () => {
      currentUser.value = null
      userContext.value = null
      useAuthStore().authToken = null
      showAdminInterface.value = false
      sessionStorage.removeItem('authToken')
    }
    const authenticate = async () => {
      clearLocalSession()
      try {
        userContext.value = await fetchUserContext(LOCAL_AUTH_TOKEN)
        currentUser.value = { ...LOCAL_AUTH_USER }
        sessionStorage.setItem('authToken', LOCAL_AUTH_TOKEN)
        authError.value = ''
        await nextTick()
        return true
      }
      catch (error) {
        authError.value = 'Local login failed. Check that the local backend is running with local authentication enabled.'
        console.error('Local login failed', error)
        return false
      }
    }
    clearLocalSession()
    if (!isPublicReport() && sessionStorage.getItem('localAuthLoggedOut') !== 'true') {
      await authenticate()
    }
    return {
      provide: {
        isLocalAuth: true,
        login: async () => {
          sessionStorage.removeItem('localAuthLoggedOut')
          if (await authenticate()) {
            const destination = attemptedPath.value || '/projects'
            attemptedPath.value = null
            await navigateTo(destination)
          }
        },
        acquireTokenSilent: async () => {
          if (isPublicReport() || !currentUser.value || !userContext.value)
            return null
          return { accessToken: LOCAL_AUTH_TOKEN }
        },
        getAccounts: () => currentUser.value ? [LOCAL_AUTH_USER] : [],
        logout: async () => {
          clearLocalSession()
          advisorCountry.value = {}
          clearAnalyticsUserId(useGtag().gtag)
          sessionStorage.setItem('localAuthLoggedOut', 'true')
          await navigateTo('/')
        },
      },
    }
  }

  const msal = new PublicClientApplication(config)
  await msal.initialize()

  // The app has no MSAL v5 redirect bridge page, so the hidden-iframe renewal
  // step can never succeed and only waits out its 10 s timeout. Stop after the
  // refresh token so an expired session fails fast.
  const silentCacheLookupPolicy = CacheLookupPolicy.AccessTokenAndRefreshToken
  let reauthenticating: Promise<never> | null = null
  // If sign-in still leaves the session needing interaction (e.g. a
  // Conditional Access policy the user cannot satisfy), redirecting again on
  // return would loop. Fall back to the sign-in page instead.
  const REAUTH_STARTED_AT_KEY = 'reauthRedirectStartedAt'
  const REAUTH_LOOP_WINDOW_MS = 60_000
  const recentlyReauthenticated = () => {
    const startedAt = Number(sessionStorage.getItem(REAUTH_STARTED_AT_KEY))
    return Date.now() - startedAt < REAUTH_LOOP_WINDOW_MS
  }

  // Acquire a separate token specifically for Microsoft Graph
  const acquireGraphToken = async (interactive = true) => {
    try {
      const account = msal.getAllAccounts()
      if (account.length > 0) {
        const graphTokenRequest = {
          scopes: ['User.Read'],
          account: account[0],
          cacheLookupPolicy: silentCacheLookupPolicy,
        }
        const graphTokenResponse
          = await msal.acquireTokenSilent(graphTokenRequest)
        return graphTokenResponse.accessToken
      }
    }
    catch (error) {
      if (!interactive) {
        return null
      }
      console.error('Failed to acquire Graph token:', error)
      // Optionally trigger interactive login for Graph
      try {
        const account = msal.getAllAccounts()
        const graphTokenResponse = await msal.acquireTokenPopup({
          scopes: ['User.Read'],
          account: account[0],
        })
        return graphTokenResponse.accessToken
      }
      catch (popupError) {
        console.error('Failed to acquire Graph token via popup:', popupError)
        return null
      }
    }
  }

  const getUserData = async () => {
    // Get a separate token for Microsoft Graph
    const graphToken = await acquireGraphToken()

    if (!graphToken) {
      console.error('No Graph token available')
      return null
    }

    const userData = await fetch(graphConfig.graphMeEndpoint, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${graphToken}`,
      },
    }).then(response => response.json())

    return userData
  }

  try {
    const response = await msal.handleRedirectPromise()
    if (response) {
      const loginToken = response.accessToken
      sessionStorage.setItem('authToken', loginToken)

      const [userData, context] = await Promise.all([
        getUserData(),
        fetchUserContext(loginToken),
      ])
      currentUser.value = userData
      userContext.value = context
      authError.value = ''
      attemptedPath.value = null
    }
  }
  catch (error) {
    authError.value = 'Login failed. Please try again.'
    console.error('Login redirect failed', error)
  }

  // Refresh on every load: persisted profile data can belong to an old account.
  // Public report viewers must not inherit the advisor's country.
  if (
    !window.location.pathname.startsWith('/web-report/')
    && !new URLSearchParams(window.location.search).has('reportAccessToken')
  ) {
    advisorCountry.value = await fetchAdvisorCountry(() => acquireGraphToken(false))
  }

  const login = async () => {
    sessionStorage.removeItem('authToken')
    sessionStorage.removeItem(REAUTH_STARTED_AT_KEY)
    authError.value = ''
    sessionStorage.setItem('attemptedPath', attemptedPath.value || '/projects')
    try {
      await msal.loginRedirect({
        scopes: [`${appConfig.AZURE_CLIENT_ID}/.default`],
      })
    }
    catch (err) {
      if (err) {
        authError.value = 'Login failed. Please try again.'
        console.error('Login failed', err)
      }
    }
  }

  const acquireTokenSilent = async ({ interactive = true } = {}) => {
    if (
      import.meta.client
        && window.location.pathname.startsWith('/web-report/')
    ) {
      return null
    }

    try {
      const account = msal.getAllAccounts()
      if (account.length > 0) {
        const tokenRequest = {
          scopes: [`${appConfig.AZURE_CLIENT_ID}/.default`],
          account: account[0],
          cacheLookupPolicy: silentCacheLookupPolicy,
        }
        const tokenResponse = await msal.acquireTokenSilent(tokenRequest)
        if (tokenResponse.accessToken) {
          sessionStorage.setItem('authToken', tokenResponse.accessToken)
          sessionStorage.removeItem(REAUTH_STARTED_AT_KEY)
          return tokenResponse
        }
        else if (interactive) {
          navigateTo('/')
        }
      }
      else if (interactive) {
        navigateTo('/')
      }
    }
    catch (error) {
      // Leave recovery to the next user action.
      if (!interactive)
        return null
      if (
        error instanceof InteractionRequiredAuthError
        && (reauthenticating || !recentlyReauthenticated())
      ) {
        // The session expired: sign in again and come back to this page.
        // Callers wait on the redirect instead of sending unauthenticated requests.
        if (!reauthenticating) {
          sessionStorage.setItem(
            'attemptedPath',
            `${window.location.pathname}${window.location.search}`,
          )
          sessionStorage.setItem(REAUTH_STARTED_AT_KEY, String(Date.now()))
          reauthenticating = msal
            .acquireTokenRedirect({
              scopes: [`${appConfig.AZURE_CLIENT_ID}/.default`],
              account: msal.getAllAccounts()[0],
            })
            .then(() => new Promise<never>(() => {}))
            .catch((redirectError: unknown) => {
              // e.g. interaction_in_progress: let a later call try again.
              reauthenticating = null
              sessionStorage.removeItem(REAUTH_STARTED_AT_KEY)
              throw redirectError
            })
        }
        return reauthenticating.catch(() => {
          sessionStorage.removeItem('authToken')
          userContext.value = null
          authError.value = 'Please sign in again.'
          if (window.location.pathname !== '/')
            navigateTo('/')
          return undefined
        })
      }
      sessionStorage.removeItem('authToken')
      userContext.value = null
      authError.value = 'Your session has expired. Please sign in again.'
      if (window.location.pathname !== '/')
        navigateTo('/')
    }
  }

  const getAccounts = () => {
    return msal.getAllAccounts()
  }

  const logout = async () => {
    advisorCountry.value = {}
    clearAnalyticsUserId(useGtag().gtag)
    try {
      // Don't acquire a token just to log out: with an expired session there
      // is no token, and logout must still work.
      const currentAccount
        = msal.getActiveAccount() ?? msal.getAllAccounts()[0] ?? null
      sessionStorage.removeItem('authToken')
      showAdminInterface.value = false
      await msal.logoutRedirect({ account: currentAccount })
    }
    catch (err) {
      if (err)
        authError.value = 'Error logging out'
    }
  }

  return {
    provide: {
      isLocalAuth: false,
      login,
      acquireTokenSilent,
      getAccounts,
      logout,
    },
  }
})
